import { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import {
	type TeamsPermissions,
	type TeamsPermissionsService,
	useTeamsPermissionsService,
} from '@atlassian/teams-common/use-teams-permissions';

import { logException } from '../../../../common/utils/sentry';
import { useV3OrgIdStatus } from '../../../org/hooks';
import { usePrincipalUser } from '../../../principal-user';
import { useSite } from '../../../site/hooks';

export type { TeamsPermissions };

const useTeamPermissionsAnalytics = ({
	data,
	error,
	permissions,
}: ReturnType<TeamsPermissionsService>) => {
	const { fireEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (error) {
			logException(error, 'Error loading team permissions in useTeamsPermissionsService');

			const { status, path } =
				error instanceof Error ? { status: 'unknown', path: 'unknown' } : error;

			fireEvent('operational.teamsPermissionsRequest.failed', {
				message: error.message,
				status,
				path,
			});
		}
	}, [error, fireEvent]);

	useEffect(() => {
		if (data && permissions) {
			fireEvent('operational.teamsPermissionsRequest.succeeded', {
				canCreateTeams: permissions.canCreateTeams,
				canViewTeams: permissions.canViewTeams,
				canAdminTeams: !!permissions.canAdminTeams,
			});
		}
	}, [data, fireEvent, permissions]);
};

export const useTeamVisibility = (): {
	isLoading: boolean;
	permissions: TeamsPermissions;
} => {
	const { orgId } = useV3OrgIdStatus();
	const [{ cloudId }] = useSite();
	const [{ principalUserId }] = usePrincipalUser();

	const result = useTeamsPermissionsService(
		{ orgId, cloudId },
		{
			// Wait for cloudId to be available before making the request
			enabled: !!cloudId,
			attributes: {
				userId: principalUserId,
			},
		},
	);

	useTeamPermissionsAnalytics(result);

	return {
		isLoading: !cloudId || result.isLoading,
		permissions: result.permissions,
	};
};
